import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { callFunction } from '@/lib/api';
import type { StaffTaskEntityType, StaffTaskLink } from '@/lib/staffTasks';
import { staffTaskEntityHref } from '@/lib/staffTasks';

export interface StaffTaskEntityOption {
  entity_type: StaffTaskEntityType;
  entity_id: string;
  label: string;
  hint?: string;
  href: string | null;
}

function searchNeedle(value: string): string {
  return value.replace(/[%_,]/g, '').trim();
}

function snapshotName(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const first = typeof row.first_name === 'string' ? row.first_name.trim() : '';
  const last = typeof row.last_name === 'string' ? row.last_name.trim() : '';
  const name = `${first} ${last}`.trim();
  return name || null;
}

async function searchPatients(clinicId: string, q: string): Promise<StaffTaskEntityOption[]> {
  let query = supabase
    .from('patients')
    .select('id, first_name, last_name')
    .eq('clinic_id', clinicId)
    .eq('status', 'active')
    .order('last_name', { ascending: true })
    .limit(8);
  const needle = searchNeedle(q);
  if (needle) {
    query = query.or(`first_name.ilike.%${needle}%,last_name.ilike.%${needle}%`);
  }
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []).map((row) => {
    const label = `${row.first_name} ${row.last_name}`.trim() || row.id;
    return {
      entity_type: 'patient' as const,
      entity_id: row.id,
      label,
      href: staffTaskEntityHref({ entity_type: 'patient', entity_id: row.id }),
    };
  });
}

async function searchInvoices(clinicId: string, q: string): Promise<StaffTaskEntityOption[]> {
  let query = supabase
    .from('invoices')
    .select('id, invoice_number, patient_snapshot')
    .eq('clinic_id', clinicId)
    .order('created_at', { ascending: false })
    .limit(8);
  const needle = searchNeedle(q);
  if (needle) {
    query = query.or(`invoice_number.ilike.%${needle}%`);
  }
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? [])
    .map((row) => {
      const number = row.invoice_number?.trim() || row.id.slice(0, 8);
      const patient = snapshotName(row.patient_snapshot);
      return {
        entity_type: 'invoice' as const,
        entity_id: row.id,
        label: number,
        hint: patient ?? undefined,
        href: staffTaskEntityHref({ entity_type: 'invoice', entity_id: row.id }),
      };
    })
    .filter((row) => {
      if (!q) return true;
      const hay = `${row.label} ${row.hint ?? ''}`.toLowerCase();
      return hay.includes(q.toLowerCase());
    });
}

async function searchReferrals(clinicId: string, q: string): Promise<StaffTaskEntityOption[]> {
  let query = supabase
    .from('referrals')
    .select('id, first_name, last_name')
    .eq('clinic_id', clinicId)
    .order('received_at', { ascending: false })
    .limit(8);
  const needle = searchNeedle(q);
  if (needle) {
    query = query.or(`first_name.ilike.%${needle}%,last_name.ilike.%${needle}%`);
  }
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []).map((row) => {
    const label = `${row.first_name} ${row.last_name}`.trim() || row.id;
    return {
      entity_type: 'referral' as const,
      entity_id: row.id,
      label,
      href: staffTaskEntityHref({ entity_type: 'referral', entity_id: row.id }),
    };
  });
}

async function searchAppointments(clinicId: string, q: string): Promise<StaffTaskEntityOption[]> {
  const { data, error } = await supabase
    .from('appointments')
    .select(
      'id, starts_at, service_name, patient:patients!appointments_patient_id_fkey(first_name,last_name)',
    )
    .eq('clinic_id', clinicId)
    .neq('status', 'cancelled')
    .order('starts_at', { ascending: false })
    .limit(20);
  if (error) throw error;
  type Row = {
    id: string;
    starts_at: string;
    service_name: string | null;
    patient: { first_name: string | null; last_name: string | null } | null;
  };
  const needle = q.toLowerCase();
  return ((data ?? []) as unknown as Row[])
    .map((row) => {
      const patient = `${row.patient?.first_name ?? ''} ${row.patient?.last_name ?? ''}`.trim();
      const when = row.starts_at.slice(0, 16).replace('T', ' ');
      return {
        entity_type: 'appointment' as const,
        entity_id: row.id,
        label: patient || row.service_name || when,
        hint: [when, row.service_name].filter(Boolean).join(' · ') || undefined,
        href: staffTaskEntityHref({ entity_type: 'appointment', entity_id: row.id }),
      };
    })
    .filter((row) => {
      if (!needle) return true;
      return `${row.label} ${row.hint ?? ''}`.toLowerCase().includes(needle);
    })
    .slice(0, 8);
}

async function searchLetters(clinicId: string, q: string): Promise<StaffTaskEntityOption[]> {
  try {
    const result = await callFunction<
      Record<string, unknown>,
      {
        items?: Array<{
          communicationIdentifier?: string;
          topic?: string | null;
          patientName?: string | null;
        }>;
      }
    >('communicate-list', { clinicId, limit: 40, offset: 0 });
    const needle = q.toLowerCase();
    return (result.items ?? [])
      .filter((item) => typeof item.communicationIdentifier === 'string')
      .map((item) => {
        const id = item.communicationIdentifier as string;
        return {
          entity_type: 'letter' as const,
          entity_id: id,
          label: item.topic?.trim() || item.patientName?.trim() || id,
          hint: item.patientName?.trim() || undefined,
          href: staffTaskEntityHref({ entity_type: 'letter', entity_id: id }),
        };
      })
      .filter((row) => {
        if (!needle) return true;
        return `${row.label} ${row.hint ?? ''}`.toLowerCase().includes(needle);
      })
      .slice(0, 8);
  } catch {
    return [];
  }
}

async function searchEmployees(clinicId: string, q: string): Promise<StaffTaskEntityOption[]> {
  const { data, error } = await supabase
    .from('clinic_memberships')
    .select('user_id, user:users!clinic_memberships_user_id_fkey(name,email)')
    .eq('clinic_id', clinicId)
    .eq('status', 'active');
  if (error) throw error;
  type Row = { user_id: string; user: { name: string | null; email: string } | null };
  const needle = q.toLowerCase();
  return ((data ?? []) as unknown as Row[])
    .map((row) => ({
      entity_type: 'employee' as const,
      entity_id: row.user_id,
      label: row.user?.name?.trim() || row.user?.email || row.user_id,
      href: staffTaskEntityHref({ entity_type: 'employee', entity_id: row.user_id }),
    }))
    .filter((row) => !needle || row.label.toLowerCase().includes(needle))
    .slice(0, 8);
}

const SEARCHERS: Record<
  StaffTaskEntityType,
  (clinicId: string, q: string) => Promise<StaffTaskEntityOption[]>
> = {
  patient: searchPatients,
  invoice: searchInvoices,
  referral: searchReferrals,
  letter: searchLetters,
  appointment: searchAppointments,
  employee: searchEmployees,
};

export function useStaffTaskEntitySearch(
  clinicId: string | null | undefined,
  entityType: StaffTaskEntityType,
  query: string,
) {
  const q = query.trim();
  return useQuery({
    queryKey: ['staff-task-entity-search', clinicId, entityType, q] as const,
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<StaffTaskEntityOption[]> => {
      if (!clinicId) return [];
      return SEARCHERS[entityType](clinicId, q);
    },
    staleTime: 15_000,
  });
}

async function labelsForLinks(
  clinicId: string,
  links: readonly StaffTaskLink[],
): Promise<StaffTaskEntityOption[]> {
  const grouped = new Map<StaffTaskEntityType, string[]>();
  for (const link of links) {
    const list = grouped.get(link.entity_type) ?? [];
    list.push(link.entity_id);
    grouped.set(link.entity_type, list);
  }

  const out: StaffTaskEntityOption[] = [];

  const patientIds = grouped.get('patient') ?? [];
  if (patientIds.length > 0) {
    const { data } = await supabase
      .from('patients')
      .select('id, first_name, last_name')
      .eq('clinic_id', clinicId)
      .in('id', patientIds);
    for (const id of patientIds) {
      const row = data?.find((item) => item.id === id);
      const label = row ? `${row.first_name} ${row.last_name}`.trim() : id;
      out.push({
        entity_type: 'patient',
        entity_id: id,
        label: label || id,
        href: staffTaskEntityHref({ entity_type: 'patient', entity_id: id }),
      });
    }
  }

  const invoiceIds = grouped.get('invoice') ?? [];
  if (invoiceIds.length > 0) {
    const { data } = await supabase
      .from('invoices')
      .select('id, invoice_number, patient_snapshot')
      .eq('clinic_id', clinicId)
      .in('id', invoiceIds);
    for (const id of invoiceIds) {
      const row = data?.find((item) => item.id === id);
      out.push({
        entity_type: 'invoice',
        entity_id: id,
        label: row?.invoice_number?.trim() || id.slice(0, 8),
        hint: snapshotName(row?.patient_snapshot) ?? undefined,
        href: staffTaskEntityHref({ entity_type: 'invoice', entity_id: id }),
      });
    }
  }

  const referralIds = grouped.get('referral') ?? [];
  if (referralIds.length > 0) {
    const { data } = await supabase
      .from('referrals')
      .select('id, first_name, last_name')
      .eq('clinic_id', clinicId)
      .in('id', referralIds);
    for (const id of referralIds) {
      const row = data?.find((item) => item.id === id);
      out.push({
        entity_type: 'referral',
        entity_id: id,
        label: row ? `${row.first_name} ${row.last_name}`.trim() || id : id,
        href: staffTaskEntityHref({ entity_type: 'referral', entity_id: id }),
      });
    }
  }

  const appointmentIds = grouped.get('appointment') ?? [];
  if (appointmentIds.length > 0) {
    const { data } = await supabase
      .from('appointments')
      .select(
        'id, starts_at, service_name, patient:patients!appointments_patient_id_fkey(first_name,last_name)',
      )
      .eq('clinic_id', clinicId)
      .in('id', appointmentIds);
    type Row = {
      id: string;
      starts_at: string;
      service_name: string | null;
      patient: { first_name: string | null; last_name: string | null } | null;
    };
    for (const id of appointmentIds) {
      const row = ((data ?? []) as unknown as Row[]).find((item) => item.id === id);
      const patient = `${row?.patient?.first_name ?? ''} ${row?.patient?.last_name ?? ''}`.trim();
      const when = row?.starts_at?.slice(0, 16).replace('T', ' ');
      out.push({
        entity_type: 'appointment',
        entity_id: id,
        label: patient || row?.service_name || when || id,
        hint: when,
        href: staffTaskEntityHref({ entity_type: 'appointment', entity_id: id }),
      });
    }
  }

  const employeeIds = grouped.get('employee') ?? [];
  if (employeeIds.length > 0) {
    const { data } = await supabase.from('users').select('id, name, email').in('id', employeeIds);
    for (const id of employeeIds) {
      const row = data?.find((item) => item.id === id);
      out.push({
        entity_type: 'employee',
        entity_id: id,
        label: row?.name?.trim() || row?.email || id,
        href: null,
      });
    }
  }

  const letterIds = grouped.get('letter') ?? [];
  if (letterIds.length > 0) {
    const searched = await searchLetters(clinicId, '');
    for (const id of letterIds) {
      const match = searched.find((row) => row.entity_id === id);
      out.push(
        match ?? {
          entity_type: 'letter',
          entity_id: id,
          label: id,
          href: staffTaskEntityHref({ entity_type: 'letter', entity_id: id }),
        },
      );
    }
  }

  return out;
}

export function useStaffTaskLinkLabels(
  clinicId: string | null | undefined,
  links: readonly StaffTaskLink[],
) {
  const key = links.map((link) => `${link.entity_type}:${link.entity_id}`).sort().join('|');
  return useQuery({
    queryKey: ['staff-task-link-labels', clinicId, key] as const,
    enabled: Boolean(clinicId && links.length > 0),
    queryFn: async (): Promise<StaffTaskEntityOption[]> => {
      if (!clinicId) return [];
      return labelsForLinks(clinicId, links);
    },
    staleTime: 30_000,
  });
}
